import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Alert,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  EmojiEvents,
  Info
} from '@mui/icons-material';

// Stableford Point System
const STABLEFORD_POINTS = {
  DOUBLE_EAGLE: 5,    // 3 under par
  EAGLE: 4,           // 2 under par  
  BIRDIE: 3,          // 1 under par
  PAR: 2,             // Par
  BOGEY: 1,           // 1 over par
  DOUBLE_BOGEY_OR_WORSE: 0  // 2+ over par
};

export default function StablefordScoreboard() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchData(true);
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, tournamentId]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      const leaderboardData = calculateStablefordLeaderboard(tournamentData, playersData);
      setLeaderboard(leaderboardData);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error fetching scoreboard data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const calculateStablefordPoints = (score, par, handicap = 0, holeIndex = 18) => {
    // Calculate playing handicap for the hole
    const holesWithStrokes = Math.floor(handicap / 18);
    const extraStrokes = handicap % 18;
    const strokesForHole = holesWithStrokes + (holeIndex <= extraStrokes ? 1 : 0);
    
    // Adjust par for this player on this hole
    const adjustedPar = par + strokesForHole;
    
    // Calculate difference
    const diff = adjustedPar - score;
    
    // Return points based on Stableford system
    if (diff >= 3) return STABLEFORD_POINTS.DOUBLE_EAGLE;
    if (diff === 2) return STABLEFORD_POINTS.EAGLE;
    if (diff === 1) return STABLEFORD_POINTS.BIRDIE;
    if (diff === 0) return STABLEFORD_POINTS.PAR;
    if (diff === -1) return STABLEFORD_POINTS.BOGEY;
    return STABLEFORD_POINTS.DOUBLE_BOGEY_OR_WORSE;
  };

  const calculateStablefordLeaderboard = (tournament, players) => {
    const scores = tournament.scores || {};
    const coursePar = tournament.golfCourse?.totalPar || 72;
    const holePars = tournament.golfCourse?.holes || Array(18).fill(4); // Default to par 4

    const playersWithScores = players
      .filter(player => scores[player.id])
      .map(player => {
        const playerScores = scores[player.id];
        const holes = Object.keys(playerScores);
        let totalPoints = 0;
        const holeDetails = [];

        // Calculate points for each hole
        holes.forEach(holeNum => {
          const holeNumInt = parseInt(holeNum);
          const score = parseInt(playerScores[holeNum]);
          const holePar = holePars[holeNumInt - 1]?.par || 4;
          const holeIndex = holePars[holeNumInt - 1]?.strokeIndex || holeNumInt;
          
          const points = calculateStablefordPoints(
            score,
            holePar,
            player.handicap || 0,
            holeIndex
          );
          
          totalPoints += points;
          holeDetails.push({
            hole: holeNumInt,
            score,
            par: holePar,
            points
          });
        });

        return {
          id: player.id,
          name: `${player.firstName} ${player.lastName}`,
          handicap: player.handicap || 0,
          holesPlayed: holes.length,
          totalPoints,
          holeDetails,
          scores: playerScores
        };
      });

    // Sort by total points (highest first)
    playersWithScores.sort((a, b) => b.totalPoints - a.totalPoints);

    return playersWithScores.map((player, index) => ({
      ...player,
      position: index + 1
    }));
  };

  const getPointsColor = (points) => {
    if (points >= 3) return 'success';
    if (points === 2) return 'info';
    if (points === 1) return 'warning';
    return 'default';
  };

  const getPointsLabel = (points) => {
    switch(points) {
      case 5: return 'Double Eagle';
      case 4: return 'Eagle';
      case 3: return 'Birdie';
      case 2: return 'Par';
      case 1: return 'Bogey';
      case 0: return 'Double+';
      default: return points;
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  if (tournament.type !== 'STABLEFORD') {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">
          This tournament is not configured for Stableford scoring.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/tournaments')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">{tournament.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Stableford Scoreboard
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Chip
            icon={<Refresh />}
            label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            color={autoRefresh ? 'success' : 'default'}
            variant={autoRefresh ? 'filled' : 'outlined'}
          />
          <IconButton onClick={() => fetchData()} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Stableford Points Legend */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Info />
          <Typography variant="h6">Stableford Scoring System</Typography>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Double Eagle: 5pts" size="small" color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Eagle: 4pts" size="small" color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Birdie: 3pts" size="small" color="success" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Par: 2pts" size="small" color="info" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Bogey: 1pt" size="small" color="warning" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Chip label="Double+: 0pts" size="small" color="default" />
          </Grid>
        </Grid>
      </Paper>

      {/* Tournament Info */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Players</Typography>
              <Typography variant="h6">{leaderboard.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Leader Points</Typography>
              <Typography variant="h6" color="success.main">
                {leaderboard[0]?.totalPoints || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Course</Typography>
              <Typography variant="h6">
                {tournament.golfCourse?.name || 'Not set'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Last Updated</Typography>
              <Typography variant="body2">
                {lastUpdated.toLocaleTimeString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leaderboard */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" width="80px"><strong>Pos</strong></TableCell>
              <TableCell><strong>Player</strong></TableCell>
              <TableCell align="center"><strong>HCP</strong></TableCell>
              <TableCell align="center"><strong>Holes</strong></TableCell>
              <TableCell align="center"><strong>Total Points</strong></TableCell>
              <TableCell align="center"><strong>Avg Points/Hole</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">No scores recorded yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              leaderboard.map((player, index) => (
                <React.Fragment key={player.id}>
                  <TableRow
                    sx={{
                      backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {index === 0 && <EmojiEvents sx={{ color: 'gold', fontSize: 20 }} />}
                        <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                          {player.position}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {player.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{player.handicap}</TableCell>
                    <TableCell align="center">{player.holesPlayed}</TableCell>
                    <TableCell align="center">
                      <Typography variant="h5" fontWeight={index < 3 ? 'bold' : 'normal'} color="primary">
                        {player.totalPoints}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {(player.totalPoints / player.holesPlayed).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {/* Hole-by-hole breakdown (expandable) */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 1, bgcolor: 'grey.50' }}>
                      <Box display="flex" gap={0.5} flexWrap="wrap" px={2}>
                        {player.holeDetails.map(detail => (
                          <Tooltip
                            key={detail.hole}
                            title={`Hole ${detail.hole}: Score ${detail.score}, Par ${detail.par}, ${getPointsLabel(detail.points)}`}
                          >
                            <Chip
                              label={`${detail.hole}: ${detail.points}pts`}
                              size="small"
                              color={getPointsColor(detail.points)}
                              variant="outlined"
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}









